<script setup lang="ts">
import { FaButton, FaEmpty, FaIcon } from '@fantastic-admin/components'
import { computed } from 'vue'

export type AppRequestStateKind = 'disconnected' | 'empty' | 'error' | 'missing' | 'not-ready' | 'permission'

const props = withDefaults(defineProps<{
  actionLabel?: string
  compact?: boolean
  description?: string
  kind: AppRequestStateKind
  plain?: boolean
  requestId?: string
  title?: string
}>(), {
  actionLabel: '',
  compact: false,
  description: '',
  plain: false,
  requestId: '',
  title: '',
})

const emit = defineEmits<{
  action: []
}>()

const metadata = computed(() => ({
  'disconnected': { icon: 'i-ri:wifi-off-line', title: '实时连接已中断', description: '当前数据可能不是最新状态，请检查网络后重新连接。' },
  'empty': { icon: 'i-ri:inbox-2-line', title: '当前没有业务数据', description: '系统已成功完成查询，当前条件下没有匹配记录。' },
  'error': { icon: 'i-ri:error-warning-line', title: '数据加载失败', description: '系统没有成功取得业务数据，页面不会把失败结果显示为 0。' },
  'missing': { icon: 'i-ri:file-damage-line', title: '关联记录缺失', description: '来源记录仍在，但关联业务对象已经不存在或无法读取。' },
  'not-ready': { icon: 'i-ri:settings-3-line', title: '配置尚未就绪', description: '相关能力尚未完成配置或 readiness 检查。' },
  'permission': { icon: 'i-ri:lock-line', title: '当前账号无权查看', description: '该数据并非 0 条，请联系管理员补充岗位权限。' },
})[props.kind])

const containerClass = computed(() => [
  props.compact
    ? 'px-4 py-3 flex-row text-left justify-start'
    : 'px-6 py-10 flex-col text-center justify-center',
  props.plain || props.kind === 'empty'
    ? 'border-0 bg-transparent rounded-none'
    : 'border border-default bg-muted/20 rounded-4',
])
</script>

<template>
  <FaEmpty
    v-if="props.kind === 'empty' && props.actionLabel"
    :title="props.title || metadata.title"
    :description="props.description || metadata.description"
    :icon="metadata.icon"
    class="border-0 rounded-none bg-transparent"
    :class="props.compact ? 'py-4 md:py-4' : 'py-10 md:py-10'"
  >
    <FaButton variant="outline" @click="emit('action')">
      <FaIcon name="i-ri:refresh-line" />
      {{ props.actionLabel }}
    </FaButton>
  </FaEmpty>
  <FaEmpty
    v-else-if="props.kind === 'empty'"
    :title="props.title || metadata.title"
    :description="props.description || metadata.description"
    :icon="metadata.icon"
    class="border-0 rounded-none bg-transparent"
    :class="props.compact ? 'py-4 md:py-4' : 'py-10 md:py-10'"
  />
  <div v-else class="flex gap-3 items-center" :class="containerClass">
    <div class="text-muted-foreground rounded-full bg-muted flex shrink-0 items-center justify-center" :class="props.compact ? 'size-9' : 'size-12'">
      <FaIcon :name="metadata.icon" class="text-xl" />
    </div>
    <div class="min-w-0" :class="props.compact ? 'flex-1' : ''">
      <div class="text-base text-foreground font-medium" :class="props.compact ? '' : 'mt-3'">
        {{ props.title || metadata.title }}
      </div>
      <div class="text-sm text-muted-foreground mt-1 max-w-xl">
        {{ props.description || metadata.description }}
      </div>
      <div v-if="props.requestId" class="text-xs text-muted-foreground font-mono mt-2 break-all">
        请求 ID：{{ props.requestId }}
      </div>
    </div>
    <FaButton v-if="props.actionLabel" variant="outline" :class="props.compact ? 'ml-auto shrink-0' : 'mt-4'" @click="emit('action')">
      <FaIcon name="i-ri:refresh-line" />
      {{ props.actionLabel }}
    </FaButton>
  </div>
</template>
